// Background scroll lock for overlays (menus, dialogs, drawers).
//
// `body { overflow: hidden }` alone is ignored by iOS Safari: the page behind
// an open overlay still scrolls under a finger. The reliable lock is to pin the
// body with `position: fixed` at the current scroll offset, then put the page
// back where it was on release.
//
// Reference counted, because overlays nest (Edit Itinerary > Add Sites >
// filters, or the delete confirmation over Edit). Only the first lock pins the
// page and only the last unlock releases it, so closing an inner overlay never
// lets the outer one's page scroll.

type ScrollWindow = {
    scrollY: number;
    scrollTo: (options: { top: number; left?: number; behavior?: string }) => void;
    document: {
        documentElement: { style: Record<string, any> };
        body: { style: Record<string, any> };
    };
};

// body.style.overflow is deliberately left alone: layouts/default.vue still
// toggles it around some overlays, and if this saved and restored it, a
// restore landing after the layout's own reset would put 'hidden' back and
// leave the page unscrollable. Pinning the body and hiding the root's
// overflow is enough to stop the page scrolling.
const BODY_PROPS = ['position', 'top', 'left', 'right', 'width'] as const;

export function createScrollLock(getWindow: () => ScrollWindow | undefined) {
    let count = 0;
    let savedY = 0;
    let savedBody: Record<string, string> = {};
    let savedHtmlOverflow = '';

    function lock() {
        const win = getWindow();
        if (!win) return;
        count++;
        if (count > 1) return;

        const { documentElement: html, body } = win.document;
        savedY = win.scrollY || 0;
        savedBody = {};
        for (const prop of BODY_PROPS) savedBody[prop] = body.style[prop] || '';
        savedHtmlOverflow = html.style.overflow || '';

        html.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${savedY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
    }

    function unlock() {
        const win = getWindow();
        if (!win || count === 0) return;
        count--;
        if (count > 0) return;

        const { documentElement: html, body } = win.document;
        for (const prop of BODY_PROPS) body.style[prop] = savedBody[prop] || '';
        html.style.overflow = savedHtmlOverflow;
        // 'instant' so a site-wide `scroll-behavior: smooth` doesn't animate the
        // page back from the top every time an overlay closes.
        win.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
    }

    return {
        lock,
        unlock,
        isLocked: () => count > 0,
    };
}

// The one lock the app shares. A second instance would fight this one over the
// same body styles.
const shared = createScrollLock(() => (typeof window === 'undefined' ? undefined : (window as unknown as ScrollWindow)));

export const lockScroll = shared.lock;
export const unlockScroll = shared.unlock;
export const isScrollLocked = shared.isLocked;
